import React from 'react'
import currencies from './currencies.json'

export const states = [
    { name: "Alberta", tax: 5 },
    { name: "British Columbia", tax: 12 },
    { name: "Manitoba", tax: 12 },
    { name: "New Brunswick", tax: 15 },
    { name: "Newfoundland and Labrador", tax: 15 },
    { name: "Northwest Territories", tax: 5 },
    { name: "Nova Scotia", tax: 15 },
    { name: "Nunavut", tax: 5 },
    { name: "Ontario", tax: 13 },
    { name: "Quebec", tax: 15 },
    { name: "Prince Edward Island", tax: 15 },
    { name: "Saskatchewan", tax: 11 },
    { name: "Yukon", tax: 5 },
];

const crrDate = new Date();
crrDate.setDate(crrDate.getDate());
export const today = crrDate.toISOString().split('T')[0];
crrDate.setDate(crrDate.getDate() + 1);
export const tommorrow = crrDate.toISOString().split('T')[0];

// Distances are measured to the listing's own pickup point, else to the seller's pickup address.
export const listingPoint = (product) => {
    const pickup = product?.pickupLocation?.location;
    return pickup && Number(pickup.lat) && Number(pickup.lon) ? pickup : product?.sellerId?.location;
};

export const transformProduct = (product) => {
    const seller = product.sellerId;
    let price = product.pricingType === 'fixed'
        ? product.packages[0]?.options.basePrice || 0
        : product.variablePricing?.basePrice || 0;

    const sd = product.saleDiscount;
    const hasSaleDiscount = sd && sd.value > 0 && (!sd.endDate || new Date(sd.endDate) >= new Date());
    let originalPrice = price;
    let discountLabel = '';
    if (hasSaleDiscount) {
        originalPrice = price;
        if (sd.type === 'percentage') {
            price = price * (1 - sd.value / 100);
            discountLabel = `${sd.value}% OFF`;
        } else {
            price = Math.max(0, price - sd.value);
            discountLabel = `USD ${sd.value} OFF`;
        }
    }

    const hasBulkPricing = product.bulkPricing && product.bulkPricing.length > 0;
    const lowestBulkPrice = hasBulkPricing
        ? Math.min(...product.bulkPricing.map(t => t.price))
        : null;

    return {
        id: product._id,
        title: product.title,
        images: product.images,
        isDiscounted: hasSaleDiscount,
        discountLabel,
        hasBulkPricing,
        lowestBulkPrice,
        maincategory: product.maincategory,
        sellingScope: product.sellingScope || 'local',
        listingType: product.listingType || 'product',
        serviceMode: product.serviceMode || '',
        sellerDetails: {
            profileImageUrl: seller.profileImage,
            displayName: seller.displayName || seller.fullName,
            distance: 0,
            sellerAddress: seller.sellerAddress || 'Unknown',
            location: listingPoint(product)
        },
        distanceKm: product.distanceKm ?? null,
        rating: product.rating,
        reviews: product.noOfReviews,
        description: product.description,
        price,
        originalPrice,
        stock: product.stock,
        // Paid placement is disclosed on the card, so the badge needs to know
        // whether the promotion window is still open.
        isBoosted: Boolean(product.boostedUntil && new Date(product.boostedUntil) > new Date()),
    };
};

export function formatDate(date) {
    const options = { month: 'short', day: 'numeric', year: "numeric" };
    return new Date(date).toLocaleDateString(undefined, options);
}

export function formatDateTime(date) {
    const options = { month: 'short', day: 'numeric', year: '2-digit' };
    const timeOptions = { hour: '2-digit', minute: '2-digit', hour12: true };
    const formattedDate = new Date(date).toLocaleDateString(undefined, options);
    const formattedTime = new Date(date).toLocaleTimeString(undefined, timeOptions);
    return `${formattedTime} - ${formattedDate}`;
}

export function calculateTax(price, country, state) {
    if (country === "Canada") {
        const province = states.find((s) => s.name === state);
        if (province)
            return (price * province.tax) / 100;
        else
            return (price * 0.05);
    }
    else
        return (price * 0.05);
}

export const formatCurrency = (price, currency, exchangeRate) => {
    const numPrice = Number(price) || 0;
    const numRate = Number(exchangeRate) || 1;
    const amount = numPrice * numRate;
    const currencyData = currencies.find(c => c.value === currency);
    if (!currencyData) {
        return `USD ${amount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
    }
    let symbol;
    if (currency === 'USD')
        symbol = 'USD ';
    else if (currency === "CHF" || currency === "ZAR" || currency === "SEK" || currency === "AED" || currency === "SAR" || currency === "MYR" || currency === "PKR")
        symbol = `${currencyData.symbol} `;
    else
        symbol = currencyData.symbol;
    const formattedAmount = amount.toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
    });
    return `${symbol}${formattedAmount}`;
};

// Dual currency (React element): shows local currency prominently + subtle USD badge
export const formatCurrencyDual = (price, currency, exchangeRate) => {
    const numPrice = Number(price) || 0;
    const usdFormatted = `USD ${numPrice.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
    if (currency === 'USD') return usdFormatted;
    const localFormatted = formatCurrency(numPrice, currency, exchangeRate);
    return React.createElement(React.Fragment, null,
        localFormatted,
        ' ',
        React.createElement('span', { className: 'usdBadge' }, '\u2248 ', usdFormatted)
    );
};

// Dual currency (plain text): for use inside template literals, button text, title attrs, etc.
export const formatCurrencyDualText = (price, currency, exchangeRate) => {
    const numPrice = Number(price) || 0;
    const usdFormatted = `USD ${numPrice.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
    if (currency === 'USD') return usdFormatted;
    const localFormatted = formatCurrency(numPrice, currency, exchangeRate);
    return `${localFormatted} (\u2248 ${usdFormatted})`;
};

/**
 * Platform commission categories. The rate charged depends only on what was
 * sold — product vs service, retail vs B2B. Mirrors
 * backend/utils/commissionFees.js — keep the two in step.
 */
export const FEE_CATEGORIES = ['product', 'service', 'b2bProduct', 'b2bService'];

export const FEE_CATEGORY_LABELS = {
    product: 'Products',
    service: 'Services',
    b2bProduct: 'B2B products',
    b2bService: 'B2B services',
};

export const feeCategoryOf = ({ isB2B, listingType } = {}) => {
    const isService = listingType === 'service';
    if (isB2B) return isService ? 'b2bService' : 'b2bProduct';
    return isService ? 'service' : 'product';
};

export const feePercentFrom = (matrix, category) => Number(matrix?.[category]) || 0;

/** The rate a seller will be charged on a listing of this kind. */
export const sellerFeePercentFor = (matrix, { isB2B, listingType } = {}) =>
    feePercentFrom(matrix, feeCategoryOf({ isB2B, listingType }));

/**
 * The commission an order was actually placed under. Orders carry the rate they
 * were charged, so it stays correct even after an admin changes the fee
 * settings; the fallback only covers orders placed before rates were recorded.
 */
export const orderSellerFeePercent = (orderOrSummary, fallbackPercent = 0) => {
    const snapshot = Number(
        orderOrSummary?.summary?.sellerFeePercent ?? orderOrSummary?.sellerFeePercent
    );
    return Number.isFinite(snapshot) ? snapshot : (Number(fallbackPercent) || 0);
};

// Seller amount after fees: grossAmount = price*quantity + deliveryFee, seller gets grossAmount * (1 - sellerFeePercent/100)
export const calculateSellerAmount = (grossAmount, sellerFeePercent) => {
    const gross = Number(grossAmount) || 0;
    const fee = Number(sellerFeePercent) || 0;
    const result = gross * (1 - fee / 100);
    return Number.isFinite(result) ? parseFloat(result.toFixed(2)) : 0;
};

// Format USD amount to local currency preview for input fields (e.g. "≈ PKR 28,000")
export const formatUsdToLocal = (usdAmount, currency, exchangeRate) => {
    if (currency === 'USD' || !currency) return '';
    const numAmount = Number(usdAmount) || 0;
    if (numAmount <= 0) return '';
    return `≈ ${formatCurrency(numAmount, currency, exchangeRate)}`;
};

// Haversine formula: calculate distance in KM between two lat/lon points
export const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Earth radius in km
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return Math.round(R * c);
};